import { Box, Button, Stack, Typography } from '@mui/material'
import { ActionProgress } from './components/ActionProgress'
import { ProvidersAutocomplete } from './components/Autocomplete'
import { IncludeCheckbox } from './components/Checkbox'
import { useDeleteForm } from './hooks/useDeleteForm'
import { useDeleteProvider } from './hooks/useDeleteProvider'
import { useLoading } from './hooks/useLoading'
import type { PageProps } from './types'

export function DeleteProvider({ options, openModal, sx }: PageProps) {
  const filteredOptions = options.filter((p) => p.ownsMedia === false)
  const { form, update, reset } = useDeleteForm(filteredOptions)
  const { deleteProvider } = useDeleteProvider()
  const { deleteLoading, loading } = useLoading()
  const disabled = loading || !form.provider

  const onClick = () => {
    const { provider } = form
    if (!provider) return
    openModal(
      <Box>
        <Typography gutterBottom>
          Do you wish to delete provider{' '}
          <span style={{ fontWeight: 600 }}>{provider.displayName}</span>, and
          all selected options?
        </Typography>
        <Typography>
          Note: If a checkbox is not selected, the associated information will
          be retained.
        </Typography>
      </Box>,
      async () => {
        const ok = await deleteProvider({
          cwsId: provider.cwsId,
          includeCollections: form.includeCollections,
          includeNotifications: form.includeNotifications,
          includeSavedSearch: form.includeSavedSearch,
        })
        if (ok) reset()
      },
    )
  }

  return (
    <Stack gap={2}>
      <Box>
        <Typography variant='h4'>Delete Provider</Typography>
        <Typography variant='caption'>
          Deletes the selected provider. Any checked options will also delete
          that content owned by the provider. Only non-shared and non-published
          collections will be deleted.
        </Typography>
      </Box>

      <Stack direction='row' gap={2}>
        <ProvidersAutocomplete
          options={filteredOptions}
          value={form.provider}
          onChange={(v) => update({ provider: v })}
        />
        <Stack direction='row' sx={{ width: 420 }}>
          <IncludeCheckbox
            label='Collections'
            checked={form.includeCollections}
            onChange={(v) => update({ includeCollections: v })}
          />
          <IncludeCheckbox
            label='Notifications'
            checked={form.includeNotifications}
            onChange={(v) => update({ includeNotifications: v })}
          />
          <IncludeCheckbox
            label='Saved Search'
            checked={form.includeSavedSearch}
            onChange={(v) => update({ includeSavedSearch: v })}
          />
        </Stack>
        <Button
          name='delete'
          variant='contained'
          size='large'
          color='error'
          disabled={disabled}
          onClick={onClick}
          sx={sx}
        >
          {deleteLoading ? <ActionProgress label='Deleting' /> : 'Delete'}
        </Button>
      </Stack>
    </Stack>
  )
}
